import React, { useState, useRef, useEffect } from "react";
import IRecipientDetailsProps from "@src/models/props/RecipientDetailsProps.interface";
import { GiftCardSendTypeEnum } from "../../models/enums/GiftCardSendTypeEnum";
import { AppSettingContext } from "../Application";
import { IRecipientDetails } from "../../models/RecipientDetails.model";
import { isEmail, hasXSSCharacters } from "../../helpers/Utils";
import PhoneInput, { CountryData } from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { PHONE_NUMBER, PhoneValidationRegexKeys } from "../../constants/global";

const RecipientDetails = (props: IRecipientDetailsProps) => {
    const ref = useRef(true);

    const { labels } = React.useContext(AppSettingContext);

    const [firstName, setFirstName] = useState(props.model.firstName);
    const [lastName, setLastName] = useState(props.model.lastName);
    const [email, setEmail] = useState(props.model.email);
    const [phone, setPhone] = useState(props.model.phone);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const phoneNumberMaxLength = PHONE_NUMBER.VALIDATIONS.MAXLENGTH;

    const phoneInputDropdowns = document.querySelectorAll('.flag-dropdown') as NodeListOf<HTMLElement>;
    const phoneNumberErrorMessages = document.querySelectorAll('.invalid-number-message') as NodeListOf<HTMLElement>;

    useEffect(() => {
        const firstRender = ref.current;
        if (firstRender) {
            ref.current = false;
        }
    }, []);

    useEffect(() => {
        if (phoneInputDropdowns && phoneInputDropdowns.length > 0) {
            phoneInputDropdowns.forEach(dropdown => {
                dropdown.style.borderColor = '#1f2937';
                dropdown.style.borderRightColor = '#cacaca';
            });
        }

        if (phoneNumberErrorMessages && phoneNumberErrorMessages.length > 0) {
            phoneNumberErrorMessages.forEach(errorMessage => {
                errorMessage.style.left = '0';
                errorMessage.style.top = '42px';
                errorMessage.classList.add("react-input-error-message");
            });
        }
    }, [phoneInputDropdowns, phoneNumberErrorMessages]);

    useEffect(() => {
        const firstRender = ref.current;
        if (firstRender == false) {
            propagateChanges();
        } 
    }, [firstName, lastName, email, phone]);

    const propagateChanges = () => {
        props.handleModelUpdate({
            firstName: firstName,
            lastName: lastName,
            email: email,
            phone: phone
        } as IRecipientDetails);
    };

    if (error) throw error;
    return (
        <div className="recipient fadeIn">
            <h6 className="fadeIn">{labels.get("GiftCard.App.Steps.Recepients.Fields.Recipient")} {props.index}</h6>
            <div className="flex-container">
                <div className="form-field" style={{ flex: '1 1 48%' }}>
                    <label htmlFor="recipients__first-name" className="label-fs">{labels.get("GiftCard.App.Steps.Recepients.Fields.RecipientFirstName.Label")}</label>
                    <input type="text"
                        value={firstName || ''}
                        onChange={event => {
                            if (hasXSSCharacters(event.target.value)) {
                                event.preventDefault();
                            } else {
                                if (!event.target.value.trim()) {
                                    props.model.firstNameIsValid = false;
                                } else {
                                    props.model.firstNameIsValid = true;
                                }

                                setFirstName(event.target.value);
                            }
                        }}
                        maxLength={150}
                        name="recipients__first-name" id="recipients__first-name"
                        placeholder={labels.get("GiftCard.App.Steps.Recepients.Fields.RecipientFirstName.Placeholder")}
                        className={props.model.firstNameIsValid === false ? "react-input-error" : ""}
                    />
                    {props.model.firstNameIsValid === false &&
                        <div className="react-input-error-message">
                            {labels.get("GiftCard.App.Steps.Recepients.Fields.RecipientFirstName.ErrorMessage")}
                        </div>
                    }
                </div>
                <div className="form-field" style={{ flex: '1 1 48%' }}>
                    <label htmlFor="recipients__last-name" className="label-fs">{labels.get("GiftCard.App.Steps.Recepients.Fields.RecipientLastName.Label")}</label>
                    <input type="text"
                        value={lastName || ''}
                        onChange={event => {
                            if (hasXSSCharacters(event.target.value)) {
                                event.preventDefault();
                            } else {
                                if (!event.target.value.trim()) {
                                    props.model.lastNameIsValid = false;
                                } else {
                                    props.model.lastNameIsValid = true;
                                }

                                setLastName(event.target.value);
                            }
                        }}
                        maxLength={150}
                        name="recipients__last-name" id="recipients__last-name"
                        placeholder={labels.get("GiftCard.App.Steps.Recepients.Fields.RecipientLastName.Placeholder")}
                        className={props.model.lastNameIsValid === false ? "react-input-error" : ""}
                    />
                    {props.model.lastNameIsValid === false &&
                        <div className="react-input-error-message">
                            {labels.get("GiftCard.App.Steps.Recepients.Fields.RecipientLastName.ErrorMessage")}
                        </div>
                    }
                </div>

                {props.sendBy == GiftCardSendTypeEnum.BY_EMAIL &&
                    <div className="form-field full__width fadeIn">
                        <label htmlFor="recipients__email" className="label-fs">{labels.get("GiftCard.App.Steps.Recepients.Fields.RecipientEmail.Label")}</label>
                        <input type="text"
                            value={email || ''}
                            maxLength={150}
                            onChange={event => {
                                if (hasXSSCharacters(event.target.value)) {
                                    event.preventDefault();
                                } else {
                                    if (!isEmail(event.target.value)) {
                                        props.model.emailIsValid = false;
                                    } else {
                                        props.model.emailIsValid = true;
                                    }

                                    setEmail(event.target.value);
                                }
                            }}
                            name="recipients__email" id="recipients__email"
                            placeholder={labels.get("GiftCard.App.Steps.Recepients.Fields.RecipientEmail.Placeholder")}
                            className={props.model.emailIsValid === false ? "react-input-error" : "" }
                        />
                        {props.model.emailIsValid === false &&
                            <div className="react-input-error-message">
                                {labels.get("GiftCard.App.Steps.Recepients.Fields.RecipientEmail.ErrorMessage")}
                            </div>
                        }
                    </div>
                }

                {props.sendBy == GiftCardSendTypeEnum.BY_SMS &&
                    <div className="form-field fadeIn" style={{ flex: '1 1 48%' }}>
                        <label htmlFor="recipients__phone" className="label-fs">{labels.get("GiftCard.App.Steps.Recepients.Fields.RecipientPhone.Label")}</label>
                        <div className="recipients__phone">
                            <PhoneInput
                                placeholder="Enter phone number"
                                country={PHONE_NUMBER.DEFAULT_COUNTRY.toLowerCase()}
                                onlyCountries={Object.keys(PHONE_NUMBER.COUNTRIES).map(c => c.toLowerCase())}
                                countryCodeEditable={false}
                                value={phone}
                                onChange={(phone) => {
                                    if (phone.length > phoneNumberMaxLength) {
                                        phone = phone.slice(0, phoneNumberMaxLength)
                                    }
                                    
                                    setPhone(`+${phone}`);
                                }}
                                isValid={(inputNumber, data: CountryData) => {
                                    if (!data || !data.iso2) {
                                        return false;
                                    }

                                    const allowedCountriesAbbr = Object.keys(PHONE_NUMBER.COUNTRIES).map(c => c.toLowerCase());
                                    if (allowedCountriesAbbr.includes(data.iso2.toLowerCase())) {
                                        const regexKey = data.iso2.toUpperCase() as PhoneValidationRegexKeys;
                                        const regex = PHONE_NUMBER.VALIDATIONS.REGEX[regexKey] as RegExp;

                                        if (regex) {
                                            return regex.test(phone);
                                        }
                                    }

                                    return false;
                                }}
                                inputClass='react-phone-input'
                                defaultErrorMessage={labels.get("GiftCard.App.Steps.Recepients.Fields.RecipientPhone.ErrorMessage")}
                                enableLongNumbers={phoneNumberMaxLength}
                            />
                        </div>
                    </div>
                }
            </div>
        </div>
    )
}

export default RecipientDetails;